import React, { useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import {
  AppButton,
  AnimatedTextInput,
  AmountAndConversion,
  StickyHeaderNavigation,
} from '@/components'
import { Avatar } from '@/components/Avatar'
import { useMultipleRecipientsStore } from '@/store/useMultipleRecipientsStore'
import { useSendStore } from '@/store/useSendStore'
import { useHandleMultipleRequests } from '@/features/send-request/hooks/useHandleMultipleRequests'
import type { RecipientParams } from '@/types/sendRequest'
import { currencyUtils } from '@/utils'
import useOffsets from '@/hooks/useOffsets'
import { getAvatarProps } from '@/utils/avatarUtils'

const { formatCryptoAmount } = currencyUtils

export const MultipleRecipientsConfirmScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()

  const note = useSendStore((s) => s.note)
  const setNote = useSendStore((s) => s.setNote)

  const selectedRecipients = useMultipleRecipientsStore(
    (s) => s.selectedRecipients,
  )
  const clearSelection = useMultipleRecipientsStore((s) => s.clearSelection)

  // Get request data from location state
  const requestData = location.state as RecipientParams | undefined

  // Extract data
  const cryptoAmount = requestData
    ? {
        amount: requestData.currencyValue || '0',
        code: requestData.currencyCode || 'USDT',
      }
    : { amount: '0', code: 'USDT' }

  const fiatAmount = requestData
    ? {
        amount: requestData.fiatValue || '0',
        code: requestData.fiatCode || 'USD',
      }
    : { amount: '0', code: 'USD' }

  const type = requestData?.type || 'request'

  // Redirect if no recipients selected (only check once on mount)
  const hasCheckedRecipients = React.useRef(false)
  useEffect(() => {
    if (!hasCheckedRecipients.current) {
      hasCheckedRecipients.current = true
      if (selectedRecipients.length === 0) {
        navigate('/send-request/recipient-selection', { replace: true })
      }
    }
  }, []) // Only run once on mount

  // Use the new hook for multiple requests
  const { handleSendMultipleRequests, isLoading } = useHandleMultipleRequests({
    type,
    currencyValue: cryptoAmount.amount,
    currencyCode: cryptoAmount.code,
    fiatAmount: fiatAmount.amount,
    fiatCode: fiatAmount.code,
  })

  const handleConfirm = async () => {
    const result = await handleSendMultipleRequests()

    if (result) {
      clearSelection()
    }
  }

  const handleBackPress = () => {
    navigate(-1)
  }

  if (selectedRecipients.length === 0) {
    return null
  }

  return (
    <BaseView
      flex={1}
      bgColor="background.screen"
      style={{
        height: '100vh',
      }}
    >
      <StickyHeaderNavigation
        title={t('form:actions.request', { defaultValue: 'Request' })}
        onBackPress={handleBackPress}
      />

      {/* Scrollable Content */}
      <BaseView
        flex={1}
        px={12}
        pt={0}
        pb={0}
        style={{
          paddingTop: `${offsets.top + 56}px`,
          overflowY: 'auto',
          paddingBottom: '120px',
        }}
      >
        {/* Header */}
        <BaseView mt={14} pb={18}>
          <BaseText
            fontSize={16}
            fontFamily="semibold"
            color="label.textTextInput"
            mb={12}
          >
            {t('translation:requestingFrom', {
              defaultValue: 'Requesting from',
            })}
          </BaseText>
        </BaseView>

        {/* Amount Display */}

        {/* Recipients List */}
        <BaseView gap={12} mb={24}>
          {selectedRecipients.map((recipient, index) => {
            const avatarProps = getAvatarProps({
              first_name: recipient.name.split(' ')[0],
              last_name: recipient.name.split(' ')[1] || '',
              avatar_path: recipient.avatar,
              avatarBg: recipient.avatarBg,
              id: recipient.id,
            })

            return (
              <BaseView
                key={`recipient-${recipient.id}-${index}`}
                bgColor="background.cardItemRoot"
                row
                align="center"
                gap={12}
                style={{
                  padding: '12px',
                  borderRadius: '24px',
                  minHeight: '72px',
                }}
              >
                <Avatar
                  size={48}
                  initials={avatarProps.initials}
                  imageUrl={avatarProps.avatarUrl}
                  gradientColors={avatarProps.backgroundGradient}
                  fullSize={false}
                />
                <BaseView flex={1}>
                  <BaseText
                    fontSize={16}
                    fontFamily="semibold"
                    color="label.textTextInput"
                  >
                    {recipient.name}
                  </BaseText>
                  {recipient.username && (
                    <BaseText
                      fontSize={14}
                      fontFamily="regular"
                      color="label.primary"
                    >
                      @{recipient.username}
                    </BaseText>
                  )}
                </BaseView>
              </BaseView>
            )
          })}
        </BaseView>
        <BaseView mt={14} pb={18}>
          <BaseText
            fontSize={16}
            fontFamily="semibold"
            color="label.textTextInput"
            mb={12}
          >
            {t('translation:amount', {
              defaultValue: 'Amount',
            })}
          </BaseText>
        </BaseView>
        <BaseView gap={14} mb={24}>
          <AmountAndConversion
            cryptoAmount={{
              amount: formatCryptoAmount({
                value: Number(cryptoAmount.amount),
                cryptoCode: cryptoAmount.code,
                showDecimals: true,
                showCryptoCode: false,
              }),
              code: cryptoAmount.code,
            }}
            fiatAmount={fiatAmount}
            showDecimals={true}
            shouldMaskStablecoinAsFiat={false}
            containerStyle={{
              alignItems: 'flex-start',
            }}
          />
        </BaseView>
        {/* Note Input */}
        <BaseView pt={24}>
          <AnimatedTextInput
            label={t('translation:leaveNote', 'Leave a note')}
            value={note}
            onChangeText={(newNote) => {
              setNote(newNote.replace(/\n/g, ''))
            }}
            maxLength={150}
            showCount
            isTextArea
            style={{
              minHeight: '100px',
            }}
          />
        </BaseView>
      </BaseView>

      {/* Fixed Bottom Button */}
      <BaseView
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          paddingBottom: `${offsets.bottom + 16}px`,
          backgroundColor: '#131415',
        }}
      >
        <AppButton
          label={t('translation:sendRequests', {
            count: selectedRecipients.length,
            defaultValue: `Request from ${selectedRecipients.length} recipients`,
          })}
          onPress={handleConfirm}
          isLoading={isLoading}
          disabled={isLoading}
        />
      </BaseView>
    </BaseView>
  )
}

export default MultipleRecipientsConfirmScreen
